import { useEffect, useMemo, useState } from "react";
import Modal from "@/components/base/Modal";
import { formatMoney } from "@/lib/format";
import type { CashSummary } from "@/types";

interface CloseRegisterModalProps {
  open: boolean;
  onClose: () => void;
  summary: CashSummary | null;
  processing: boolean;
  onConfirm: (counted: number) => void;
}

export default function CloseRegisterModal({
  open,
  onClose,
  summary,
  processing,
  onConfirm,
}: CloseRegisterModalProps) {
  const [counted, setCounted] = useState("");

  useEffect(() => {
    if (!open) return;
    setCounted("");
  }, [open]);

  const expected = Number(summary?.expected_total ?? 0);
  const countedNum = Number(counted) || 0;
  const difference = countedNum - expected;
  const hasCount = counted.trim().length > 0;

  const tone = useMemo(() => {
    if (!hasCount) return "bg-background-100 text-foreground-600 border-background-200";
    if (Math.abs(difference) < 0.005) return "bg-accent-50 text-accent-800 border-accent-200";
    if (difference < 0) return "bg-primary-50 text-primary-700 border-primary-200";
    return "bg-secondary-100 text-secondary-900 border-secondary-200";
  }, [difference, hasCount]);

  const diffLabel = difference < 0 ? "Faltante" : difference > 0 ? "Sobrante" : "Cuadrado";

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Cerrar caja"
      subtitle="Cuenta el efectivo real y confirma para guardar el cierre con su diferencia."
      size="sm"
      footer={
        <>
          <button
            type="button"
            onClick={onClose}
            className="flex items-center justify-center gap-2 whitespace-nowrap rounded-md border border-background-300 bg-background-50 px-4 py-2.5 text-sm font-semibold text-foreground-700 hover:bg-background-100"
          >
            Cancelar
          </button>
          <button
            type="button"
            onClick={() => onConfirm(countedNum)}
            disabled={processing || !hasCount}
            className="flex items-center justify-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-5 py-2.5 text-sm font-semibold text-background-50 hover:bg-primary-600 disabled:cursor-not-allowed disabled:opacity-50"
          >
            {processing ? (
              <i className="ri-loader-4-line animate-spin text-base"></i>
            ) : (
              <i className="ri-lock-line text-base"></i>
            )}
            Confirmar cierre
          </button>
        </>
      }
    >
      <div className="flex flex-col gap-4">
        <div className="rounded-lg border border-background-200 bg-background-100 px-4 py-3">
          <p className="text-xs text-foreground-500">Efectivo esperado en caja</p>
          <p className="font-heading mt-0.5 text-xl font-bold text-foreground-950">{formatMoney(expected)}</p>
        </div>

        <div className="flex flex-col gap-1.5">
          <label className="text-xs font-semibold text-foreground-700">Efectivo contado (RD$)</label>
          <input
            value={counted}
            onChange={(event) => setCounted(event.target.value.replace(/[^0-9.]/g, ""))}
            inputMode="decimal"
            placeholder="0.00"
            className="w-full rounded-md border border-background-300 bg-background-50 px-3 py-2.5 text-sm text-foreground-950 outline-none focus:border-primary-400"
          />
        </div>

        <div className={`rounded-lg border px-4 py-3 ${tone}`}>
          <div className="flex items-center justify-between">
            <span className="text-xs font-semibold">{hasCount ? diffLabel : "Diferencia"}</span>
            <span className="font-heading text-lg font-bold">
              {hasCount ? formatMoney(Math.abs(difference)) : "—"}
            </span>
          </div>
        </div>
      </div>
    </Modal>
  );
}